import { format, formatDistanceToNow, isToday, isThisWeek, isThisMonth, parseISO, differenceInDays, differenceInCalendarDays } from 'date-fns';

/**
 * Parse a date value safely.
 * Handles YYYY-MM-DD strings, full ISO strings (with time/timezone), and
 * Date objects (PostgreSQL DATE columns come back from the pg driver as
 * JS Date objects set to UTC midnight).
 *
 * All inputs are normalized to noon local time to prevent the common
 * off-by-one bug where UTC midnight displays as the previous day in
 * US Eastern (or any UTC-negative timezone).
 */
export function parseDate(dateVal) {
  if (!dateVal) return null;

  // pg driver returns DATE columns as JS Date objects at UTC midnight.
  // Extract the UTC year/month/day and rebuild at local noon.
  if (dateVal instanceof Date) {
    return new Date(dateVal.getUTCFullYear(), dateVal.getUTCMonth(), dateVal.getUTCDate(), 12, 0, 0);
  }

  const str = String(dateVal);

  // Date-only string (YYYY-MM-DD) -- add noon to avoid timezone shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    return parseISO(str + 'T12:00:00');
  }

  // Full ISO string (e.g., 2026-01-01T00:00:00.000Z) -- extract date part,
  // use noon to avoid the same UTC midnight shift issue
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
    return parseISO(str.substring(0, 10) + 'T12:00:00');
  }

  return parseISO(str);
}

/**
 * Format a date as "Jan 15, 2026"
 */
export function formatDate(dateStr) {
  const date = parseDate(dateStr);
  if (!date) return '-';
  return format(date, 'MMM d, yyyy');
}

/**
 * Format a date as "Jan 15"
 */
export function formatDateShort(dateStr) {
  const date = parseDate(dateStr);
  if (!date) return '-';
  return format(date, 'MMM d');
}

/**
 * Format a date range as "Jan 15 - Jan 20" or "Jan 15" if same day.
 */
export function formatDateRange(startStr, endStr) {
  if (!startStr) return '-';
  if (!endStr || startStr === endStr) return formatDateShort(startStr);
  return `${formatDateShort(startStr)} - ${formatDateShort(endStr)}`;
}

/**
 * Format a date as a relative string: "2 days ago", "in 3 days"
 */
export function formatRelative(dateStr) {
  const date = parseDate(dateStr);
  if (!date) return '-';
  return formatDistanceToNow(date, { addSuffix: true });
}

/**
 * Check if a date string is today.
 */
export function checkIsToday(dateStr) {
  const date = parseDate(dateStr);
  return date ? isToday(date) : false;
}

/**
 * Check if a date string is this week.
 */
export function checkIsThisWeek(dateStr) {
  const date = parseDate(dateStr);
  return date ? isThisWeek(date, { weekStartsOn: 1 }) : false;
}

/**
 * Check if a date string is this month.
 */
export function checkIsThisMonth(dateStr) {
  const date = parseDate(dateStr);
  return date ? isThisMonth(date) : false;
}

/**
 * Get the number of days until a date (positive = future, negative = past).
 */
export function daysUntil(dateStr) {
  const date = parseDate(dateStr);
  if (!date) return null;
  return differenceInCalendarDays(date, new Date());
}

/**
 * Get the number of days between two dates.
 */
export function daysBetween(startStr, endStr) {
  const start = parseDate(startStr);
  const end = parseDate(endStr);
  if (!start || !end) return null;
  return differenceInDays(end, start);
}

/**
 * Format a date for display in a list: "Today", "Tomorrow", "Jan 15"
 */
export function formatListDate(dateStr) {
  const days = daysUntil(dateStr);
  if (days === null) return '-';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return formatDateShort(dateStr);
}
